import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { alpha, Box, Button, Tooltip, Typography, useTheme } from '@mui/material'
import React from 'react'
import { NewDeal } from 'types'

interface ContractTotalsBarProps {
  deal: NewDeal
  monthlyTotal: number
  /**
   * Recurring-monthly summary. When `isFlighted`, the monthly amount varies
   * month to month (services ramp/start/stop at different times), so a single
   * "Monthly" figure is misleading — the bar shows "Varies" and points at the
   * payment schedule instead.
   */
  monthlyDisplay?: { isFlighted: boolean; min: number; max: number }
  implementationFee: number
  grossProfit: number
  /** Lifetime gross profit across the contract — used to compute Gross Margin %. */
  totalGrossProfit?: number
  totalPoints?: number
  setupFee: number
  total: number
  isCSA?: boolean
  paymentAmount?: number
  isEditing: boolean
  /**
   * Number of service rows on the contract. Shown so a reviewer can tell at a
   * glance whether the table above is showing everything, instead of having to
   * notice that it scrolls.
   */
  productCount?: number
  onAddProduct: () => void
}

const fmt = (v: number) =>
  v.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

const Stat = ({
  label,
  value,
  highlight,
  tooltip,
}: {
  label: string
  value: string
  highlight?: boolean
  tooltip?: string
}) => {
  const theme = useTheme()
  const box = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        bgcolor: highlight ? alpha(theme.palette.text.highlight, 0.08) : alpha(theme.palette.text.primary, 0.03),
        border: '1px solid',
        borderColor: highlight ? alpha(theme.palette.text.highlight, 0.2) : 'divider',
        cursor: tooltip ? 'help' : undefined,
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'text.secondary',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 700,
          color: highlight ? 'primary.main' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  )
  return tooltip ? <Tooltip title={tooltip}>{box}</Tooltip> : box
}

// Gross Margin = totalGrossProfit / total × 100. Falls back to monthly
// grossProfit/monthlyTotal if a lifetime number wasn't passed in (so older
// call sites still render a sensible value). Guards divide-by-zero.
const computeGrossMarginPct = (numerator: number, denominator: number): number | null => {
  if (!denominator || denominator <= 0) return null
  return (numerator / denominator) * 100
}

const ContractTotalsBar: React.FC<ContractTotalsBarProps> = ({
  deal,
  monthlyTotal,
  monthlyDisplay,
  implementationFee,
  grossProfit,
  totalGrossProfit,
  totalPoints,
  setupFee,
  total,
  isCSA,
  paymentAmount,
  isEditing,
  productCount,
  onAddProduct,
}) => {
  const grossMarginPct = computeGrossMarginPct(
    totalGrossProfit ?? grossProfit ?? 0,
    total ?? 0,
  )
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        flexWrap: 'wrap',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      {productCount != null && productCount > 0 && <Stat label="Services" value={String(productCount)} />}
      {monthlyDisplay?.isFlighted ? (
        <Stat
          label="Monthly"
          value="Varies"
          tooltip={`Monthly amount changes month to month — ${fmt(monthlyDisplay.min)} to ${fmt(
            monthlyDisplay.max,
          )}. See the payment schedule.`}
        />
      ) : (
        <Stat label="Monthly" value={fmt(monthlyTotal)} />
      )}
      <Stat label="Setup Fee" value={fmt(setupFee)} />
      {implementationFee > 0 && <Stat label="Impl. Fee" value={fmt(implementationFee)} />}
      <Stat label="Gross Profit" value={fmt(grossProfit)} />
      {totalPoints != null && totalPoints > 0 && <Stat label="Total Points" value={totalPoints.toLocaleString()} />}
      {grossMarginPct != null && (
        <Stat label="Gross Margin" value={`${grossMarginPct.toFixed(1)}%`} />
      )}
      <Stat
        label={isCSA ? 'Per-Payment' : 'Total'}
        value={isCSA && paymentAmount != null ? fmt(paymentAmount) : fmt(total)}
        highlight
      />
      {isEditing && (
        <Box sx={{ marginLeft: 'auto' }}>
          <Button
            size="small"
            sx={{ textTransform: 'none', '&:hover': { opacity: '80%' } }}
            disabled={!deal?.isDraft}
            startIcon={<AddCircleOutlineIcon />}
            onClick={onAddProduct}
          >
            Add Service
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default ContractTotalsBar
